import moment from 'moment';
import { Dayjs } from 'dayjs';
import {
  TurniExtraItemInterface,
  TurniExtraPointOfSaleInterface,
} from '../interfaces/extra-turni-user';

/**
 * Tipo per i tipi di turno
 */
type TurnoType = 'calendar' | 'shift';

/**
 * Tipo per i numeri di intervallo
 */
type RangeNumber = 1 | 2;

/**
 * Interfaccia per gli errori di un turno
 */
interface TurnoErrors {
  range1: boolean;
  range2: boolean;
}

/**
 * Interfaccia per gli errori di tutti i turni per una chiave
 */
interface ExtraHoursErrorItem {
  shift: TurnoErrors;
  calendar: TurnoErrors;
}

/**
 * Tipo per la risposta della validazione degli orari extra
 */
type ErrorExtraHoursMap = Record<string, ExtraHoursErrorItem>;

/**
 * Controlla tutti gli orari extra e restituisce gli errori
 * @param extraHours - Orari extra da controllare
 * @returns Mappa degli errori per ogni chiave
 */
export const checkAllExtraHours = (
  extraHours: TurniExtraPointOfSaleInterface
): ErrorExtraHoursMap => {
  const errorDefaultHours: ErrorExtraHoursMap = {};
  const turniTypes: TurnoType[] = ['calendar', 'shift'];

  for (const extra of Object.keys(extraHours)) {
    // Inizializza la struttura degli errori per questa chiave
    errorDefaultHours[extra] = {
      shift: {
        range1: false,
        range2: false,
      },
      calendar: {
        range1: false,
        range2: false,
      },
    };

    // Controlla ogni tipo di turno e intervallo
    for (const turnoType of turniTypes) {
      errorDefaultHours[extra][turnoType].range1 = checkExtraHoursValue(
        1,
        turnoType,
        extraHours[extra]
      );
      errorDefaultHours[extra][turnoType].range2 = checkExtraHoursValue(
        2,
        turnoType,
        extraHours[extra]
      );
    }
  }

  return errorDefaultHours;
};

/**
 * Interfaccia per l'oggetto hours
 */
interface ExtraHoursData {
  date?: Dayjs;
  shift: TurniExtraItemInterface | null;
  calendar: TurniExtraItemInterface | null;
}

/**
 * Controlla se un intervallo orario è valido
 * @param rangeNumber - Numero dell'intervallo (1 o 2)
 * @param turniType - Tipo di turno ('calendar' o 'shift')
 * @param hours - Dati degli orari da controllare
 * @returns true se c'è un errore, false se l'orario è valido
 */
export const checkExtraHoursValue = (
  rangeNumber: RangeNumber,
  turniType: TurnoType,
  hours: ExtraHoursData
): boolean => {
  const range = hours[turniType];

  // Se il range non è definito, non c'è errore
  if (!range) return false;

  // Ottiene gli orari di inizio e fine dell'intervallo
  const rangeStart = extractMomentTime(range, `startTime${rangeNumber}`);
  const rangeEnd = extractMomentTime(range, `endTime${rangeNumber}`);

  // Se entrambi gli orari sono nulli, non c'è errore
  if (!rangeStart && !rangeEnd) return false;

  // Se uno solo degli orari è nullo, c'è un errore
  if ((rangeStart && !rangeEnd) || (!rangeStart && rangeEnd)) return true;

  // Se l'orario di inizio è uguale o successivo a quello di fine, c'è un errore
  if (rangeStart!.isSameOrAfter(rangeEnd!)) return true;

  // Controlla sovrapposizione con l'altro intervallo
  if (isOverlappingWithOtherRange(range, rangeNumber, rangeStart!, rangeEnd!)) {
    return true;
  }

  // Per i turni di tipo 'shift', non ci sono altri controlli
  if (turniType === 'shift') return false;

  // Per i turni di tipo 'calendar', controlla se sono contenuti nei turni 'shift'
  return !isCalendarWithinShift(hours, rangeStart!, rangeEnd!);
};

/**
 * Estrae un orario da un oggetto e lo converte in un oggetto moment
 * @param obj - Oggetto contenente l'orario
 * @param key - Chiave dell'orario da estrarre
 * @returns Un oggetto moment se l'orario esiste, altrimenti null
 */
function extractMomentTime(
  obj: TurniExtraItemInterface,
  key: string
): moment.Moment | null {
  return key in obj && obj[key] ? moment(obj[key].toDate()) : null;
}

/**
 * Controlla se l'intervallo si sovrappone con l'altro intervallo dello stesso tipo
 * @param range - Dati dell'intervallo
 * @param currentRangeNumber - Numero dell'intervallo corrente
 * @param rangeStart - Orario di inizio
 * @param rangeEnd - Orario di fine
 * @returns true se c'è sovrapposizione, false altrimenti
 */
function isOverlappingWithOtherRange(
  range: TurniExtraItemInterface,
  currentRangeNumber: RangeNumber,
  rangeStart: moment.Moment,
  rangeEnd: moment.Moment
): boolean {
  const otherRangeNumber: RangeNumber = currentRangeNumber === 1 ? 2 : 1;

  const otherRangeStart = extractMomentTime(
    range,
    `startTime${otherRangeNumber}`
  );
  const otherRangeEnd = extractMomentTime(range, `endTime${otherRangeNumber}`);

  if (!otherRangeStart || !otherRangeEnd) return false;

  return (
    otherRangeStart.isBetween(rangeStart, rangeEnd, 'minutes', '[)') ||
    otherRangeEnd.isBetween(rangeStart, rangeEnd, 'minutes', '(]')
  );
}

/**
 * Controlla se l'intervallo calendario è contenuto in un intervallo turno
 * @param hours - Dati degli orari
 * @param calendarStart - Orario di inizio calendario
 * @param calendarEnd - Orario di fine calendario
 * @returns true se l'intervallo calendario è contenuto in un turno, false altrimenti
 */
function isCalendarWithinShift(
  hours: ExtraHoursData,
  calendarStart: moment.Moment,
  calendarEnd: moment.Moment
): boolean {
  const rangeShift = hours.shift;

  // Se non ci sono turni definiti, c'è un errore
  if (!rangeShift) return false;

  // Estrai gli orari per entrambi gli intervalli turno
  const rangeShiftStart1 = extractMomentTime(rangeShift, 'startTime1');
  const rangeShiftEnd1 = extractMomentTime(rangeShift, 'endTime1');
  const rangeShiftStart2 = extractMomentTime(rangeShift, 'startTime2');
  const rangeShiftEnd2 = extractMomentTime(rangeShift, 'endTime2');

  // Se non ci sono orari turno definiti, non c'è errore
  if (
    !rangeShiftStart1 &&
    !rangeShiftStart2 &&
    !rangeShiftEnd1 &&
    !rangeShiftEnd2
  ) {
    return false;
  }

  // Controlla se l'intervallo calendario è contenuto nel primo intervallo turno
  if (
    rangeShiftStart1 &&
    rangeShiftEnd1 &&
    calendarStart.isBetween(
      rangeShiftStart1,
      rangeShiftEnd1,
      'minutes',
      '[)'
    ) &&
    calendarEnd.isBetween(rangeShiftStart1, rangeShiftEnd1, 'minutes', '(]')
  ) {
    return true;
  }

  // Controlla se l'intervallo calendario è contenuto nel secondo intervallo turno
  if (
    rangeShiftStart2 &&
    rangeShiftEnd2 &&
    calendarStart.isBetween(
      rangeShiftStart2,
      rangeShiftEnd2,
      'minutes',
      '[)'
    ) &&
    calendarEnd.isBetween(rangeShiftStart2, rangeShiftEnd2, 'minutes', '(]')
  ) {
    return true;
  }

  // Se non è contenuto in nessun intervallo turno, c'è un errore
  return false;
}
